
if (!customElements.get('gd-mani-inspo')) {
  customElements.define('gd-mani-inspo', class extends HTMLElement {
    connectedCallback() {
      this.abort = new AbortController();
      this.busy = false;
      this.held = false;
      this.startPositioned = false;
      this.startCancelled = false;
      this.videoTimers = new Map();
      this.controlsTimers = new Map();
      this.feedbackTimers = new Map();
      this.track = this.querySelector('.gd-inspo__track');
      if (!this.track) return;
      this.addEventListener('click', event => {
        const one = event.target.closest('[data-add-one]');
        const all = event.target.closest('[data-add-all]');
        const play = event.target.closest('[data-play]');
        const review = event.target.closest('[data-review]');
        if (one || all) this.add(one || all, Boolean(all));
        if (play) this.playReel(play.closest('[data-look]'));
        if (review) {
          document.body.dispatchEvent(new CustomEvent('gd:cart-open', { bubbles: true }));
          document.body.dispatchEvent(new CustomEvent('cart:updated'));
        }
        if (event.target.closest('[data-prev]')) this.scroll(-1);
        if (event.target.closest('[data-next]')) this.scroll(1);
      }, { signal: this.abort.signal });
      this.addEventListener('change', event => {
        if (!event.target.matches('[data-variant]')) return;
        const row = event.target.closest('[data-product]');
        const selected = event.target.selectedOptions[0];
        if (!row || !selected) return;
        row.querySelector('[data-price-text]').textContent = selected.dataset.priceLabel;
        const price = Number(selected.dataset.price);
        const comparePrice = Number(selected.dataset.comparePrice);
        const onSale = Number.isFinite(price) && Number.isFinite(comparePrice) && price > 0 && comparePrice > price;
        const percent = onSale ? Math.round((comparePrice - price) / comparePrice * 100) : 0;
        const compare = row.querySelector('[data-compare-price-text]');
        const discount = row.querySelector('[data-discount-text]');
        if (compare) {
          compare.textContent = onSale ? selected.dataset.comparePriceLabel : '';
          compare.hidden = !onSale;
        }
        if (discount) {
          discount.textContent = percent > 0 ? '-' + percent + '%' : '';
          discount.hidden = percent <= 0;
        }
      }, { signal: this.abort.signal });
      document.addEventListener('visibilitychange', () => { if (document.hidden) this.pauseVideos(); }, { signal: this.abort.signal });
      const keepUserPosition = () => { this.startCancelled = true; };
      ['pointerdown', 'touchstart', 'wheel', 'keydown'].forEach(type => {
        this.track.addEventListener(type, keepUserPosition, { passive: true, signal: this.abort.signal });
      });
      this.track.addEventListener('scroll', () => this.updateArrows(), { passive: true, signal: this.abort.signal });
      this.resize = new ResizeObserver(() => { this.positionMobileStart(); this.updateArrows(); });
      this.resize.observe(this.track);
      if ('IntersectionObserver' in window) {
        this.visibility = new IntersectionObserver(entries => entries.forEach(entry => {
          if (!entry.isIntersecting) entry.target.querySelector('video')?.pause();
        }), { threshold: 0.15 });
        this.querySelectorAll('[data-look]').forEach(look => this.visibility.observe(look));
      }
      document.addEventListener('shopify:block:select', event => {
        if (this.contains(event.target)) { this.startCancelled = true; event.target.scrollIntoView({ block: 'nearest', inline: 'center', behavior: 'instant' }); }
      }, { signal: this.abort.signal });
      this.querySelectorAll('[data-play]').forEach(button => { button.hidden = false; });
      this.querySelectorAll('.gd-inspo__video-slot video').forEach(video => this.bindVideo(video.closest('[data-look]'), video));
      window.addEventListener('resize', () => this.syncVideoControls(), { signal: this.abort.signal });
      this.refreshButtons();
      this.positionMobileStart();
      this.updateArrows();
    }

    disconnectedCallback() {
      this.abort?.abort();
      this.resize?.disconnect();
      this.visibility?.disconnect();
      this.pauseVideos();
      this.videoTimers?.forEach(clearTimeout);
      this.controlsTimers?.forEach(clearTimeout);
      this.feedbackTimers?.forEach(clearTimeout);
    }

    refreshButtons() {
      this.querySelectorAll('[data-add-one]').forEach(button => { button.disabled = this.busy || this.held || button.classList.contains('is-success') || button.classList.contains('is-error'); });
      this.querySelectorAll('[data-add-all]').forEach(button => { button.disabled = this.busy || this.held || button.classList.contains('is-success') || button.classList.contains('is-error') || button.dataset.allowed !== 'true'; });
      this.querySelectorAll('[data-variant]').forEach(select => { select.disabled = this.busy || this.held; });
    }

    isMobileCarousel() {
      return !this.classList.contains('gd-inspo--feature') && matchMedia('(max-width: 749px)').matches;
    }

    positionMobileStart() {
      if (this.startPositioned || this.startCancelled || !this.isMobileCarousel()) return;
      const cards = Array.from(this.track.querySelectorAll('[data-look]'));
      if (!cards.length || this.track.clientWidth <= 0) return;
      const requested = Number.parseInt(this.dataset.mobileStartCard || '2', 10);
      const index = Math.min(cards.length - 1, Math.max(0, Math.min(5, Number.isFinite(requested) ? requested : 2) - 1));
      const rect = cards[index].getBoundingClientRect();
      if (rect.width <= 0) return;
      this.startPositioned = true;
      cards.forEach((card, cardIndex) => card.classList.toggle('is-active', cardIndex === index));
      const center = this.track.getBoundingClientRect().left + this.track.clientWidth / 2;
      const left = this.track.scrollLeft + rect.left + rect.width / 2 - center;
      this.track.scrollTo({ left: Math.max(0, Math.min(this.track.scrollWidth - this.track.clientWidth, left)), behavior: 'instant' });
    }

    nearestCardIndex() {
      const cards = Array.from(this.track.querySelectorAll('[data-look]'));
      const center = this.track.getBoundingClientRect().left + this.track.clientWidth / 2;
      let nearest = 0;
      let distance = Infinity;
      cards.forEach((card, index) => {
        const rect = card.getBoundingClientRect();
        const delta = Math.abs(rect.left + rect.width / 2 - center);
        if (delta < distance) { nearest = index; distance = delta; }
      });
      return nearest;
    }

    updateArrows() {
      const cards = Array.from(this.track.querySelectorAll('[data-look]'));
      const centered = this.isMobileCarousel();
      this.classList.toggle('gd-inspo--centered', centered);
      const overflow = this.track.scrollWidth > this.track.clientWidth + 2;
      this.querySelector('.gd-inspo__arrows').hidden = !overflow;
      if (centered) {
        const active = this.nearestCardIndex();
        cards.forEach((card, index) => {
          card.classList.toggle('is-active', index === active);
          if (index === active) card.setAttribute('aria-current', 'true');
          else {
            card.removeAttribute('aria-current');
            card.querySelector('video')?.pause();
          }
        });
        this.querySelector('[data-prev]').disabled = !overflow || active === 0;
        this.querySelector('[data-next]').disabled = !overflow || active >= cards.length - 1;
      } else {
        cards.forEach(card => { card.classList.remove('is-active'); card.removeAttribute('aria-current'); });
        this.querySelector('[data-prev]').disabled = this.track.scrollLeft <= 2;
        this.querySelector('[data-next]').disabled = this.track.scrollLeft + this.track.clientWidth >= this.track.scrollWidth - 2;
      }
    }

    scroll(direction) {
      this.startCancelled = true;
      const cards = Array.from(this.track.querySelectorAll('[data-look]'));
      if (!cards.length) return;
      const behavior = matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth';
      if (this.isMobileCarousel()) {
        const index = Math.max(0, Math.min(cards.length - 1, this.nearestCardIndex() + direction));
        const rect = cards[index].getBoundingClientRect();
        const center = this.track.getBoundingClientRect().left + this.track.clientWidth / 2;
        const left = this.track.scrollLeft + rect.left + rect.width / 2 - center;
        this.track.scrollTo({ left: Math.max(0, Math.min(this.track.scrollWidth - this.track.clientWidth, left)), behavior });
        return;
      }
      const gap = parseFloat(getComputedStyle(this.track).columnGap) || 16;
      this.track.scrollBy({ left: direction * (cards[0].getBoundingClientRect().width + gap), behavior });
    }

    pauseVideos(except) {
      this.querySelectorAll('video').forEach(video => { if (video !== except) video.pause(); });
    }

    setVideoLoading(look, loading) {
      const button = look.querySelector('[data-play]');
      button.hidden = false;
      button.disabled = loading;
      button.classList.toggle('is-loading', loading);
      button.setAttribute('aria-busy', String(loading));
      look.querySelector('.gd-inspo__media').setAttribute('aria-busy', String(loading));
    }

    clearVideoTimer(video) {
      clearTimeout(this.videoTimers.get(video));
      this.videoTimers.delete(video);
    }

    videoFailed(look, video) {
      if (video.dataset.playRequested !== 'true') return;
      video.dataset.playRequested = 'false';
      video.dataset.failed = 'true';
      this.clearVideoTimer(video);
      video.pause();
      video.controls = false;
      look.querySelector('.gd-inspo__video-slot').hidden = true;
      this.setVideoLoading(look, false);
      look.querySelector('[data-video-status]').textContent = this.dataset.videoError;
    }

    isMobileVideo() {
      return matchMedia('(max-width: 749px)').matches;
    }

    hideVideoControls(video) {
      clearTimeout(this.controlsTimers.get(video));
      this.controlsTimers.delete(video);
      if (!this.isMobileVideo()) return;
      video.controls = false;
      delete video.dataset.userControls;
      if (video.paused) video.closest('[data-look]').querySelector('[data-play]').hidden = false;
    }

    showVideoControls(video, keyboard = false) {
      if (!this.isMobileVideo()) return;
      clearTimeout(this.controlsTimers.get(video));
      this.controlsTimers.delete(video);
      video.controls = true;
      video.dataset.userControls = 'true';
      video.dataset.keyboardControls = String(keyboard);
      video.closest('[data-look]').querySelector('[data-play]').hidden = true;
      if (!video.paused && !keyboard) this.controlsTimers.set(video, setTimeout(() => this.hideVideoControls(video), 3000));
    }

    syncVideoControls() {
      this.querySelectorAll('.gd-inspo__video-slot video').forEach(video => {
        if (this.isMobileVideo()) this.hideVideoControls(video);
        else {
          clearTimeout(this.controlsTimers.get(video));
          this.controlsTimers.delete(video);
          video.controls = !video.paused;
        }
      });
    }

    bindVideo(look, video) {
      const slot = look.querySelector('.gd-inspo__video-slot');
      const button = look.querySelector('[data-play]');
      video.controls = false;
      video.tabIndex = 0;
      video.setAttribute('aria-label', button.getAttribute('aria-label') || '');
      video.addEventListener('pointerdown', () => { video.dataset.keyboardControls = 'false'; }, { passive: true, signal: this.abort.signal });
      video.addEventListener('click', event => this.showVideoControls(video, event.detail === 0), { signal: this.abort.signal });
      video.addEventListener('focus', () => this.showVideoControls(video, video.dataset.keyboardControls !== 'false'), { signal: this.abort.signal });
      video.addEventListener('keydown', () => this.showVideoControls(video, true), { signal: this.abort.signal });
      video.addEventListener('blur', () => { delete video.dataset.keyboardControls; if (!video.paused) this.showVideoControls(video, false); }, { signal: this.abort.signal });
      video.addEventListener('play', () => this.pauseVideos(video), { signal: this.abort.signal });
      video.addEventListener('playing', () => {
        video.dataset.playRequested = 'true';
        this.clearVideoTimer(video);
        slot.classList.add('is-ready');
        this.setVideoLoading(look, false);
        button.hidden = true;
        if (this.isMobileVideo()) {
          if (video.dataset.userControls === 'true') this.showVideoControls(video, video.dataset.keyboardControls === 'true');
          else video.controls = false;
        } else video.controls = true;
        look.querySelector('[data-video-status]').textContent = '';
      }, { signal: this.abort.signal });
      video.addEventListener('waiting', () => {
        if (video.dataset.playRequested !== 'true' || video.paused) return;
        this.setVideoLoading(look, true);
        this.clearVideoTimer(video);
        this.videoTimers.set(video, setTimeout(() => this.videoFailed(look, video), 20000));
      }, { signal: this.abort.signal });
      const resetPausedVideo = () => {
        video.dataset.playRequested = 'false';
        this.clearVideoTimer(video);
        this.setVideoLoading(look, false);
        clearTimeout(this.controlsTimers.get(video));
        this.controlsTimers.delete(video);
        if (this.isMobileVideo() && video.dataset.userControls === 'true') button.hidden = true;
        else video.controls = false;
      };
      video.addEventListener('pause', resetPausedVideo, { signal: this.abort.signal });
      video.addEventListener('ended', resetPausedVideo, { signal: this.abort.signal });
      video.addEventListener('error', () => this.videoFailed(look, video), { signal: this.abort.signal });
    }

    playReel(look) {
      if (!look) return;
      let video = look.querySelector('.gd-inspo__video-slot video');
      const slot = look.querySelector('.gd-inspo__video-slot');
      let shouldLoad = false;
      if (!video) {
        const original = look.querySelector('[data-reel-template]')?.content.querySelector('video');
        if (!original || !slot) return;
        video = original.cloneNode(true);
        video.muted = true;
        video.playsInline = true;
        video.controls = false;
        video.preload = 'metadata';
        slot.replaceChildren(video);
        this.bindVideo(look, video);
        shouldLoad = true;
      }
      this.pauseVideos(video);
      if (video.dataset.failed === 'true') {
        shouldLoad = true;
        slot.classList.remove('is-ready');
        delete video.dataset.failed;
      }
      slot.hidden = false;
      if (this.isMobileVideo()) this.hideVideoControls(video);
      video.dataset.playRequested = 'true';
      look.querySelector('[data-video-status]').textContent = '';
      this.setVideoLoading(look, true);
      this.clearVideoTimer(video);
      this.videoTimers.set(video, setTimeout(() => this.videoFailed(look, video), 20000));
      try {
        // Mirror the existing stories player: source + explicit load before play.
        if (shouldLoad) video.load();
        const attempt = video.play();
        if (attempt?.catch) attempt.catch(() => this.videoFailed(look, video));
      } catch (_) { this.videoFailed(look, video); }
    }

    buttonState(button, state, duration = 0) {
      clearTimeout(this.feedbackTimers.get(button));
      this.feedbackTimers.delete(button);
      button.classList.remove('is-loading', 'is-success', 'is-error');
      if (state) button.classList.add('is-' + state);
      button.setAttribute('aria-busy', String(state === 'loading'));
      if (duration) this.feedbackTimers.set(button, setTimeout(() => {
        button.classList.remove('is-loading', 'is-success', 'is-error');
        button.removeAttribute('aria-busy');
        this.feedbackTimers.delete(button);
        this.refreshButtons();
      }, duration));
    }

    rootPath() {
      const root = new URL(this.dataset.root || '/', location.origin);
      if (root.origin !== location.origin || !root.pathname.endsWith('/')) throw Error('invalid_root');
      return root.pathname;
    }

    async request(path, options) {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 15000);
      try {
        const response = await fetch(this.rootPath() + path, { credentials: 'same-origin', cache: 'no-store', ...options, signal: controller.signal });
        const body = await response.json();
        if (!response.ok || body.status) throw Error('request_failed');
        return body;
      } finally { clearTimeout(timeout); }
    }

    async add(button, all) {
      if (this.busy || this.held || button.disabled) return;
      const look = button.closest('[data-look]');
      const status = look.querySelector('[data-status]');
      const rows = all ? Array.from(look.querySelectorAll('[data-product]')) : [button.closest('[data-product]')];
      if (!rows.length || rows.some(row => row.dataset.eligible !== 'true')) return;
      this.busy = true;
      this.buttonState(button, 'loading');
      this.refreshButtons();
      look.setAttribute('aria-busy', 'true');
      status.removeAttribute('data-error');
      status.textContent = this.dataset.adding;
      let posted = false;
      try {
        const items = await Promise.all(rows.map(async row => {
          const selected = row.querySelector('[data-variant]').selectedOptions[0];
          if (!selected || !/^[1-9][0-9]*$/.test(selected.value) || Number(selected.dataset.price) <= 0) throw Error('ineligible');
          const product = await this.request('products/' + encodeURIComponent(row.dataset.handle) + '.js');
          const tags = Array.isArray(product.tags) ? product.tags : String(product.tags || '').split(',').map(tag => tag.trim());
          if (String(product.id) !== row.dataset.productId || product.handle !== row.dataset.handle ||
              tags.some(tag => ['bundle-component-only', 'gift-only', 'password-protected', 'gd-sold-out'].includes(tag)) || product.requires_selling_plan ||
              ['new-dip-perfect-complete-kit', 'instant-gels-bundle'].includes(product.handle)) throw Error('ineligible');
          const variant = product.variants?.find(candidate => String(candidate.id) === selected.value);
          if (!variant?.available || !Number.isFinite(Number(variant.price)) || Number(variant.price) <= 0) throw Error('ineligible');
          return { id: selected.value, quantity: 1 };
        }));
        if (new Set(items.map(item => item.id)).size !== items.length) throw Error('duplicate_variant');
        posted = true;
        const body = await this.request('cart/add.js', { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ items }) });
        const lines = Array.isArray(body.items) ? body.items : [];
        if (!lines.length || lines.some(line => !items.some(item => item.id === String(line.variant_id || line.id)) || !Number.isInteger(line.quantity) || line.quantity < 1) || items.some(item => !lines.some(line => String(line.variant_id || line.id) === item.id))) throw Error('unexpected_response');
        try {
          items.forEach(item => {
            const line = lines.find(candidate => String(candidate.variant_id || candidate.id) === item.id);
            window._learnq?.push(['track', 'Added to Cart', {
            $value: Number(line.price || 0) / 100 * item.quantity,
            AddedItemProductName: line.product_title || line.title || '', AddedItemProductID: String(line.product_id || ''),
            AddedItemSKU: line.sku || '', AddedItemQuantity: item.quantity, AddedItemPrice: Number(line.price || 0) / 100,
            AddedItemURL: line.url ? new URL(line.url, location.origin).href : '', AddedItemImageURL: line.image || '',
            AddedItemCategories: line.product_type ? [line.product_type] : [], AddedItemVariantTitle: line.variant_title || ''
          }]);
          });
        } catch (_) {}
        document.body.dispatchEvent(new CustomEvent('product-added-to-cart', { bubbles: true, detail: { line: body } }));
        document.body.dispatchEvent(new CustomEvent('gd:cart-open', { bubbles: true }));
        status.textContent = this.dataset.added;
        this.buttonState(button, 'success', 700);
      } catch (_) {
        status.setAttribute('data-error', '');
        this.buttonState(button, 'error', 2000);
        if (posted) {
          this.held = true;
          status.textContent = this.dataset.uncertain;
          look.querySelector('[data-review]').hidden = false;
          document.body.dispatchEvent(new CustomEvent('cart:updated'));
        } else status.textContent = this.dataset.unavailable;
      } finally {
        this.busy = false;
        look.removeAttribute('aria-busy');
        this.refreshButtons();
      }
    }
  });
}


